/**
 * Authentication utility for Gynzy.
 *
 * This module provides support for logging in using the gynzy/frontend-accounts-nuxt service using these steps:
 *
 * 1. After a related interaction, the user is redirected to the Gynzy accounts service.
 * 2. The user logs in and is redirected back to the application with an authorization code.
 * 3. The application exchanges the authorization code for access and refresh tokens, storing them in session storage.
 * 4. The application sets a timer to refresh the access token before it expires, using the refresh token.
 *
 * @module gynzyAuthProvider
 * @example
 * // Initiate the OAuth login flow:
 *
 * // Check for the presence of tokens in session storage, and refreshing the access token if necessary:
 * import { gynzyAuthProvider } from './utils/auth';
 * ...
 * gynzyAuthProvider.check();
 *
 * @note This module shares some code with and derives inspiration from the gynzy/manager gynzyAuthProvider
 * found in src/providers/gynzy-auth/provider.ts.
 */

import GraphQLClient from '@/utils/graphql-client';
import { TokenNotFoundError } from './TokenNotFoundError';
import { TokenRefreshError } from './TokenRefreshError';
import { OAuthCode } from './oauth-code';

// #region Constants
/**
 * Key used to store and retrieve the access token in session storage.
 * @type {string}
 * @constant
 */
const accessTokenKey = 'access_token';

/**
 * Key used to store and retrieve the refresh token in session storage.
 * @type {string}
 * @constant
 */
const refreshTokenKey = 'refresh_token';

/**
 * Key used to store and retrieve the token expiration timestamp in local storage.
 * @type {string}
 * @constant
 */
const accessTokenExpiresAtKey = 'access_token_expires_at';
// #endregion

// #region State

/**
 * Current user object, initialized as null.
 * This will be populated with user data after a successful authentication check.
 * @type {Object|null}
 */
let currentUser;

// #endregion

// #region Token handling functions

/**
 * Parses a JWT token from a string and returns its payload as an object.
 * @param {string} token - The JWT access token to parse.
 * @returns {Object|null} The parsed JWT token as an object, or null if the token is invalid or not provided.
 */
function _parseJwt(token) {
	if (!token) {
		return null;
	}

	const base64Url = token.split('.')[1];
	if (!base64Url) {
		return null;
	}

	const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
	const jsonPayload = decodeURIComponent(
		window
			.atob(base64)
			.split('')
			.map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
			.join('')
	);

	return JSON.parse(jsonPayload);
}

/**
 * Retrieves the access token from session storage, parses it, and returns the parsed token.
 * @returns {Object|null} The parsed access token or null if not found or invalid.
 */
function _getAccessTokenFromStorage({ parseToken } = { parseToken: false }) {
	const token = sessionStorage.getItem(accessTokenKey);
	return parseToken ? _parseJwt(token) : token;
}

/**
 * Retrieves the refresh token from session storage.
 * @returns {string|null} The refresh token or null if not found.
 */
function _getRefreshTokenFromStorage() {
	return sessionStorage.getItem(refreshTokenKey);
}

/**
 * Sets the token expiration timestamp in session storage based on the token response.
 * @param {{ expires_in: string, [key: string]: any }} tokenResponse - The token response object containing the expires_in property.
 */
function _setTokenExpiresAt(tokenResponse) {
	if (tokenResponse.expires_in) {
		tokenResponse.expires_at = new Date().getTime() + tokenResponse.expires_in * 1000;
		sessionStorage.setItem(accessTokenExpiresAtKey, tokenResponse.expires_at.toString());
	}
}

/**
 * Refresh the access token using the refresh token and set them in storage.
 * @returns {void}
 */
async function _refreshAccessToken(schoolId = null) {
	const refreshToken = _getRefreshTokenFromStorage();
	if (!refreshToken) {
		throw new TokenNotFoundError('No refresh token found');
	}

	const params = new URLSearchParams();
	params.append('grant_type', 'refresh_token');
	params.append('refresh_token', refreshToken);
	params.append('client_id', process.env.GATSBY_OAUTH_CLIENT_ID);

	const url = new URL('/oauth/token', process.env.GATSBY_ACCOUNTS_API_URL);
	if (schoolId !== null) {
		url.searchParams.append('school_id', schoolId);
	}

	const response = await fetch(url.toString(), {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
		body: params.toString(),
	});

	const data = await response.json();
	if (!data.access_token || !data.refresh_token) {
		throw new TokenRefreshError(`Failed to refresh access token: ${JSON.stringify(data)}`);
	}

	sessionStorage.setItem(accessTokenKey, data.access_token);
	sessionStorage.setItem(refreshTokenKey, data.refresh_token);

	_setTokenExpiresAt(data);

	if (data.access_token && data.refresh_token) {
		_startTokenRefreshTimer();
	}
}

/**
 * Removes the access and refresh tokens from session storage.
 * This effectively logs out the user by clearing their authentication state.
 * @returns {void}
 */
function _removeTokensFromStorage() {
	sessionStorage.removeItem(accessTokenKey);
	sessionStorage.removeItem(refreshTokenKey);
}

// Variables that holds the timer. Scoped outside of the function to allow for
// subsequent calls to the function without creating multiple timers.
let tokenRefreshTimer;

/**
 * Checks if a token refresh timer is already running. If not, it calculates the time until the access token expires
 * and sets a timer to refresh the access token before it expires.
 * The timer is set to refresh the token 30 minutes before it expires..
 * @returns {void}
 */
function _startTokenRefreshTimer() {
	if (tokenRefreshTimer) return;

	const expiresAt = sessionStorage.getItem(accessTokenExpiresAtKey);
	const minute = 60 * 1000;
	const gracePeriod = 30 * minute; // 30 minutes in milliseconds
	const timerDuration = expiresAt - Date.now() - gracePeriod;

	tokenRefreshTimer = setTimeout(async () => {
		tokenRefreshTimer = null; // Reset the timer after refreshing

		try {
			await _refreshAccessToken();
		} catch (e) {
			if (e instanceof TokenNotFoundError) {
				console.error('Token not found:', e.message);
				_removeTokensFromStorage();
				tokenRefreshTimer = null; // Reset the timer after refreshing
			} else if (e instanceof TypeError || e instanceof TokenRefreshError) {
				if (expiresAt > Date.now() + minute) {
					console.warn('Failed to refresh access token:', e.message, '\n\nTrying again in 1 minute.');
					setTimeout(() => {
						_startTokenRefreshTimer();
					}, minute);
				} else {
					console.error('Failed to refresh access token:', e);
					_removeTokensFromStorage();
				}
			} else {
				console.error('An unexpected error occurred while refreshing the access token:', e);
				_removeTokensFromStorage();
			}
		}
	}, timerDuration);
}
// #endregion

// #region Backend interaction functions

async function _getCurrentUser() {
	const graphqlClient = new GraphQLClient(process.env.GATSBY_GATEWAY_GRAPHQL_URL, {
		Authorization: `Bearer ${_getAccessTokenFromStorage()}`,
		'x-ratelimit-id': _getAccessTokenFromStorage({ parseToken: true })['ratelimit-id'],
	});

	const result = await graphqlClient.postQuery(`
		query {
			currentUser {
				roles {
					type
					schoolId
				}
				schools {
					id
					nationalIdentifier
					name
					address {
						addressLine1
						addressLine2
						zipCode
						city
					}
				}
			}
		}
	`);

	currentUser = result.data.currentUser;
}

// #endregion

// #region Exported Auth Provider
/**
 * Authentication provider.
 * This provider provides mechanisms to check if the user is authenticated,
 * and to log out the user by clearing the tokens from the session storage.
 * @typedef {Object} gynzyAuthProvider
 * @property {Function} check - Checks if the user is authenticated.
 * @property {Function} logout - Logs out the user by removing the access and refresh tokens from session storage.
 */
export const gynzyAuthProvider = {
	/**
	 * Gets the login URL for the specified locale. This URL is used to redirect the user to the authentication service.
	 * This function proxies the call to [OAuthCode.initiateAuthentication] so that [gynzyAuthProvider] fully covers
	 * the authentication flow.
	 * @param {string} locale - The locale to be used for the login URL.
	 * @returns {Promise<string>} - The URL to redirect the user to for login.
	 * @throws {Error} - If the initiation of the OAuth flow fails.
	 */
	getLoginUrl: async locale => await OAuthCode.initiateAuthentication(locale),
	/**
	 * Gets the login origin URL stored in the OAuth state. This URL is used to redirect the user back to the original page
	 * they were on before starting the authentication process if login fails. This function proxies the call to
	 * [OAuthCode.getLoginOriginUrl] so that [gynzyAuthProvider] fully covers the authentication flow.
	 * @returns {string} - The login origin URL.
	 */
	getLoginOriginUrl: () => OAuthCode.getLoginOriginUrl(),
	/**
	 * Logs in the user by exchanging the OAuth authorization code for access and refresh tokens.
	 * The tokens are stored in session storage, and a timer is started to refresh the access token before it expires.
	 * @returns {Promise<{locale: string}>} An object containing the locale from the OAuth state.
	 * @throws {Error} If the login process fails.
	 */
	login: async () => {
		const { response, locale } = await OAuthCode.exchangeCodeForTokens();
		const tokenResponse = await response.json();

		sessionStorage.setItem(accessTokenKey, tokenResponse.access_token);
		sessionStorage.setItem(refreshTokenKey, tokenResponse.refresh_token);

		_setTokenExpiresAt(tokenResponse);
		_startTokenRefreshTimer();

		return { locale };
	},
	/**
	 * Checks if the user is authenticated by looking for tokens in session storage.
	 * If tokens are found, it starts a timer to refresh the access token before it expires.
	 * @returns {{authenticated: boolean, error?: {message: string, name: string}}}
	 */
	check: () => {
		const token = _getAccessTokenFromStorage();
		const refreshToken = _getRefreshTokenFromStorage();

		if (!token || !refreshToken) {
			return {
				authenticated: false,
				error: {
					message: 'Check failed',
					name: token ? 'Refresh token not found' : 'Token not found',
				},
			};
		}

		_startTokenRefreshTimer();

		return {
			authenticated: true,
		};
	},
	/**
	 * Logs out the user by removing the access and refresh tokens from session storage.
	 * @returns {void}
	 */
	logout: () => {
		_removeTokensFromStorage();
	},
	isLoggedIn: () => !!_getAccessTokenFromStorage(),
	getAccessToken: ({ parseToken } = { parseToken: false }) => _getAccessTokenFromStorage({ parseToken }),
	/**
	 * Fetches a new token based on the provided school ID.
	 * @returns {Promise<boolean>} Returns true if the token was successfully refreshed, false otherwise.
	 */
	refreshTokenForSchool: async schoolId => {
		try {
			await _refreshAccessToken(schoolId);
			return true;
		} catch (e) {
			console.error('Failed to refresh token for school:', e);
			return false;
		}
	},
	getCurrentUser: async () => {
		if (!currentUser) {
			await _getCurrentUser();
		}

		return currentUser;
	},
};
